import { Box, Typography, Stack, debounce } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import LoaderComponent from 'components/Loader';
import { useEffect, useRef, useState } from 'react';
import { DoughnutChartWidgetProps } from './type';
import { getDoughnutChartOptions } from './chartWidgetUtility';
import { ECElementEvent } from 'echarts';

const defaultOptions = {
	graphic: [
		{
			type: 'circle',
			left: 'center',
			top: 'center',
			shape: {
				r: 100
			},
			style: {
				fill: '#71758f66'
			}
		},
		{
			type: 'circle',
			left: 'center',
			top: 'center',
			shape: {
				r: 60  
			},
			style: {
				fill: '#ffffff'  
			}
		},
	],
	title: {
		show: true,
		textStyle: {
			color: '#bcbcbc',
			fontWeight: 'normal',
			fontSize: 16
		},
		text: 'No data',
		left: 'center',
		top: 'center'
	},
	backgroundColor: 'transparent'
};

const getGridLeft = (containerWidth: number): string => {
  if (containerWidth < 420) return '18%';
  if (containerWidth < 600) return '15%';
  if (containerWidth < 820) return '12%';
  if (containerWidth < 920) return '9%';
  if (containerWidth < 1260) return '7%';
  return '5%';
};

const DoughnutChartWidget = ({
	isLoading = false,
	isOverlayWidget = false,
	title = '',
	totalText,
	seriesData,
	minTitleHeight = '50.5px',
	handleEvent,
	dataLabel = false,
	legendLabel = false,
	hoursFormat = false,
	sectorLabel = '',
	chartSize,
	isRMCost = false
}: Readonly<DoughnutChartWidgetProps>) => {
	const chartContainerRef = useRef<HTMLDivElement>(null);
	const [windowSize, setWindowSize] = useState(window.innerWidth);
	const [gridLeft, setGridLeft] = useState('5%');

	useEffect(() => {
		// re-render chart to fix alignment when resizing the browser
		const handleResize = debounce(() => {
			if (chartContainerRef.current) {
				const containerWidth = chartContainerRef.current.offsetWidth;
				const calculatedLeft = getGridLeft(containerWidth);
				setGridLeft(calculatedLeft);
				setWindowSize(window.innerWidth);
			}
		}, 200);

		handleResize();
		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const result = getDoughnutChartOptions(totalText, seriesData, dataLabel, legendLabel, hoursFormat, title);

	const adjustedOption: any = !seriesData?.length
		? defaultOptions
		: {
				...result,
				grid: {
					...result.graphic,
					left: gridLeft
				}
		  };

	let computedMinHeight = '500px';

	if (chartSize) {
		if (isRMCost) {
			computedMinHeight =
			window.innerWidth >= 1075 ? chartSize : '500px';
		} else {
			computedMinHeight = chartSize;
		}
	}

	return (
		<Box display="flex" flexGrow={1} height="100%" position="relative">
			<Stack
				minHeight="100px"
				p={2}
				bgcolor="#ffffff"
				fontFamily="Montserrat"
				borderRadius="5px"
				flexGrow={1}
				// {...(handleEvent && { onClick: handleEvent, sx: { cursor: 'pointer' } })}
			>
				<Stack direction="row" gap={2} justifyContent="space-between" alignItems="start" minHeight={minTitleHeight}>
					<Stack direction="row" gap={1.5} alignItems="start">
						<Typography variant={isOverlayWidget ? 'overlayWidgetTitle' : 'widgetTitle'} sx={{ color: '#000' }}>
							{title}
						</Typography>
					</Stack>
					{sectorLabel != '' && (
							<Box bgcolor="blue.darker" sx={{
								borderRadius: '16px',
								width: 'auto',
								float: 'right',
								display: 'flex',
								height: 'auto',
								alignItems: 'center',
								padding: '5px'
							}}>
								<Typography variant="bodyText" sx={{ textAlign: 'center' }}>{sectorLabel}</Typography>
							</Box>
					)}
				</Stack>
				<Stack flexGrow={1} justifyContent="center" key={windowSize} ref={chartContainerRef} minHeight={computedMinHeight}>
					{!isLoading ? (
						<ReactECharts
							style={{ width: '100%', height: '100%', bottom: '6%' }}
							option={adjustedOption}
							opts={{ renderer: 'canvas', width: 'auto' }}
							notMerge={true}
							lazyUpdate={true}	
							onEvents={{
								click: (params: ECElementEvent) => {
									if(handleEvent){
										handleEvent(params, title);
									}
								}
							}}						
						/>
					) : (
						<LoaderComponent />
					)}
				</Stack>
			</Stack>
		</Box>
	);
};

export default DoughnutChartWidget;
